import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Table,
    Badge,
    Spinner,
    Alert,
    FormGroup,
    Label,
    Input,
    FormFeedback
} from "reactstrap";
import { toast } from "react-toastify";

interface Fournisseur {
    id: string;
    nom: string;
    code_fournisseur: string;
}

interface OrderLine {
    id: string;
    rowid: string;
    fk_product: string;
    product_label: string;
    qty: string;
    fk_commandefourndet?: string;
}

interface OrderDetails {
    id: string;
    ref: string;
    lines: OrderLine[];
    expeditions?: {
        [key: string]: string;
    };
}

interface User {
    entity: string;
    token: string;
}

interface ValidationChoiceModalProps {
    isOpen: boolean;
    toggle: () => void;
    orderDetails: OrderDetails | null;
    user: User;
    onValidateNormal: () => void;
    onValidateFournisseur: (success: boolean, message: string) => void;
    loading: boolean;
    minQte?: number;
    maxQte?: number;
}

const ValidationChoiceModal: React.FC<ValidationChoiceModalProps> = ({
    isOpen,
    toggle,
    orderDetails,
    user,
    onValidateNormal,
    onValidateFournisseur,
    loading,
    minQte = 1,
    maxQte = 1000
}) => {
    const [step, setStep] = useState<number>(1);
    const [fournisseurs, setFournisseurs] = useState<Fournisseur[]>([]);
    const [selectedFournisseurs, setSelectedFournisseurs] = useState<{[key: string]: string}>({});
    const [quantities, setQuantities] = useState<{[key: string]: number}>({});
    const [loadingFournisseurs, setLoadingFournisseurs] = useState<boolean>(false);
    const [fournisseursError, setFournisseursError] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [successModalData, setSuccessModalData] = useState({
        message: "",
        orderRefs: [] as string[],
        supplierOrdersCount: 0,
        successLinesCount: 0,
        errorLinesCount: 0
    });
   

    // Filter lines: hide lines with fk_commandefourndet and where remaining quantity is 0
    const filteredLines = useMemo(() => {
        if (!orderDetails || !orderDetails.lines) return [];
        
        return orderDetails.lines.filter((line: OrderLine) => {
            // Check if line has fk_commandefourndet
            if (line.fk_commandefourndet && line.fk_commandefourndet !== null) {
                return false;
            }
            
            // Calculate remaining quantity
            const orderedQty = parseInt(line.qty) || 0;
            const preparedQty = orderDetails.expeditions ? 
                parseInt(orderDetails.expeditions[line.rowid] || "0") || 0 : 
                0;
            
            // Only show if remaining quantity is > 0
            return (orderedQty - preparedQty) > 0;
        });
    }, [orderDetails]);

    // Reset when modal opens
    useEffect(() => {
        if (isOpen) {
            setStep(1);
            setSelectedFournisseurs({});
            setQuantities({});
            setValidationErrors({});
            setFournisseursError(null);
        }
    }, [isOpen]);

    // Load fournisseurs when step changes to 2
    useEffect(() => {
        if (step === 2 && isOpen && orderDetails) {
            fetchFournisseurs();
        }
    }, [step, isOpen, orderDetails]);

    // Initialize quantities for filtered lines
    useEffect(() => {
        if (orderDetails && filteredLines.length > 0) {
            const initialQuantities: {[key: string]: number} = {};
            const initialSelections: {[key: string]: string} = {};
            
            filteredLines.forEach((line: OrderLine) => {
                // Calculate remaining quantity
                const orderedQty = parseInt(line.qty) || 0;
                const preparedQty = orderDetails.expeditions ? 
                    parseInt(orderDetails.expeditions[line.rowid] || "0") || 0 : 
                    0;
                const remainingQty = Math.max(0, orderedQty - preparedQty);
                
                initialQuantities[line.rowid] = remainingQty;
            });
            
            setQuantities(initialQuantities);
            
            // Also initialize fournisseur selections if we have fournisseurs already
            if (fournisseurs.length > 0) {
                filteredLines.forEach((line: OrderLine) => {
                    initialSelections[line.rowid] = fournisseurs[0].id;
                });
                setSelectedFournisseurs(initialSelections);
            }
        }
    }, [orderDetails, filteredLines, fournisseurs]);

    const fetchFournisseurs = async () => {
        setLoadingFournisseurs(true);
        setFournisseursError(null);

        try {
            const response : any  = await axios.post<{
                success: boolean;
                fournisseurs?: Fournisseur[];
                message?: string;
            }>(
                "https://api.apapiecesauto.fr/apiV2/",
                {
                    action: "getDataFournisseurs",
                    entity: user.entity
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${user.token}`
                    }
                }
            );

            if (response.success) {
                const fetchedFournisseurs = response.fournisseurs;
                setFournisseurs(fetchedFournisseurs);
                
                // Initialize selections with the first fournisseur for filtered lines
                const initialSelections: {[key: string]: string} = {};
                filteredLines.forEach((line: OrderLine) => {
                    if (fetchedFournisseurs.length > 0) {
                        initialSelections[line.rowid] = fetchedFournisseurs[0].id;
                    }
                });
                setSelectedFournisseurs(initialSelections);
            } else {
                setFournisseursError(response.message || "Impossible de charger les fournisseurs");
            }
        } catch (error) {
            console.error("Error fetching fournisseurs:", error);
            setFournisseursError("Erreur lors du chargement des fournisseurs");
        } finally {
            setLoadingFournisseurs(false);
        }
    };

    // Function to validate QTE
    const validateQte = (lineId: string, qte: number): boolean => {
        const errors = {...validationErrors};
        
        if (isNaN(qte) || qte < minQte || qte > maxQte) {
            errors[lineId] = `La quantité doit être entre ${minQte} et ${maxQte}`;
            setValidationErrors(errors);
            return false;
        } else {
            delete errors[lineId];
            setValidationErrors(errors);
            return true;
        }
    };

    // Function to handle QTE change
    const handleQteChange = (lineId: string, value: string) => {
        const numValue = parseInt(value) || 0;
        
        if (validateQte(lineId, numValue)) {
            setQuantities((prev: {[key: string]: number}) => ({
                ...prev,
                [lineId]: numValue
            }));
        } else {
            setQuantities((prev: {[key: string]: number}) => ({
                ...prev,
                [lineId]: numValue
            }));
        }
    };

    // Prepare QTE for submission
    const prepareQteForSubmission = (lineId: string): number => {
        const qte = quantities[lineId] || 0;
        return Math.max(minQte, Math.min(maxQte, qte));
    };

    const handleChoice = (choice: 'normal' | 'fournisseur') => {
        if (choice === 'normal') {
            onValidateNormal();
            toggle();
        } else if (choice === 'fournisseur') {
            setStep(2);
        }
    };

    const handleFournisseurChange = (lineId: string, fournisseurId: string) => {
        setSelectedFournisseurs((prev: {[key: string]: string}) => ({
            ...prev,
            [lineId]: fournisseurId
        }));
    };

    // Check if all QTEs are valid before submission
    const isAllQteValid = (): boolean => {
        return filteredLines.every((line: OrderLine) => {
            const qte = quantities[line.rowid] || 0;
            return qte >= minQte && qte <= maxQte;
        });
    };

    // Fonction pour fermer le modal de succès et rafraîchir la page
    const handleCloseSuccessModal = () => {
        setSuccessModalOpen(false);
        // Rafraîchir après 500ms pour permettre la fermeture du modal
        setTimeout(() => {
            window.location.reload();
        }, 500);
    };
    
    const handleValidateWithFournisseurs = async () => {
        if (!orderDetails || filteredLines.length === 0) {
            return;
        }
        
        if (!isAllQteValid()) {
            toast.error("Veuillez corriger les quantités avant de valider");
            return;
        }

        try {
            const linesWithFournisseurs = filteredLines.map((line: OrderLine) => ({
                line_id: parseInt(line.rowid),
                product_id: parseInt(line.fk_product),
                qty: prepareQteForSubmission(line.rowid),
                supplier_id: parseInt(selectedFournisseurs[line.rowid])
            }));

            // Validation des données avant envoi
            const invalidLines = linesWithFournisseurs.filter(line => 
                isNaN(line.line_id) || 
                isNaN(line.product_id) || 
                isNaN(line.supplier_id) || 
                isNaN(line.qty) ||
                line.line_id <= 0 ||
                line.product_id <= 0 ||
                line.supplier_id <= 0 ||
                line.qty <= 0
            );

            if (invalidLines.length > 0) {
                toast.error(`${invalidLines.length} ligne(s) contiennent des données invalides`);
                return;
            }

            const response  : any = await axios.post(
                "https://api.apapiecesauto.fr/apiV2/",
                {
                    action: "createordrefourfromline",
                    entity: user.entity,
                    order_id: parseInt(orderDetails.id),
                    lines: linesWithFournisseurs
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${user.token}`
                    }
                }
            );

            // TRAITEMENT DE LA RÉPONSE
            if (response && typeof response.success === 'boolean') {
                if (response.success) {
                    // SUCCÈS GLOBAL
                    const lines = response.data?.line || [];
                    const supplierOrders = response.data?.supplier_orders || [];
                    
                    const successLines = lines.filter((item: any) => 
                        item.result && item.result.success === true
                    );
                    
                    const errorLines = lines.filter((item: any) => 
                        item.result && item.result.success === false
                    );
                    
                    // Préparer les données pour le modal de succès
                    const orderRefs = supplierOrders.map((order: any) => order.ref);
                    
                    // Mettre à jour les données du modal
                    setSuccessModalData({
                        message: "Traitement terminé avec succès",
                        orderRefs: orderRefs,
                        supplierOrdersCount: supplierOrders.length,
                        successLinesCount: successLines.length,
                        errorLinesCount: errorLines.length
                    });
                    
                    // Appeler la fonction callback avec les données
                    onValidateFournisseur(true, "Traitement terminé avec succès");
                    
                    // Fermer le modal de validation
                    toggle();
                    
                    // Ouvrir le modal de succès
                    setSuccessModalOpen(true);
                    
                } else {
                    // ÉCHEC GLOBAL - Afficher un toast d'erreur
                    let errorMessage = response.data.message || "Erreur lors de la validation";
                    
                    const lines = response.data.data?.line || [];
                    const errorLines = lines.filter((item: any) => 
                        item.result && item.result.success === false
                    );
                    
                    if (errorLines.length > 0) {
                        const errorDetails = errorLines.map((errorLine: any) => {
                            const lineInfo = `Ligne ${errorLine.line_id}`;
                            const errorInfo = errorLine.result?.error || "Erreur inconnue";
                            const errorCode = errorLine.result?.error_code ? ` (code: ${errorLine.result.error_code})` : '';
                            return `${lineInfo}: ${errorInfo}${errorCode}`;
                        }).join('; ');
                        
                        errorMessage += ` - Détails: ${errorDetails}`;
                    }
                    
                    onValidateFournisseur(false, errorMessage);
                    toast.error(errorMessage);
                }
            } else {
                onValidateFournisseur(false, "Réponse inattendue du serveur");
                toast.error("Réponse inattendue du serveur");
            }
            
        } catch (error: any) {
            let errorMessage = "Erreur lors de la validation";
            
            if (error.response) {
                if (error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response.data && typeof error.response.data === 'string') {
                    errorMessage = error.response.data;
                } else if (error.response.data && error.response.data.success === false) {
                    errorMessage = error.response.data.message || errorMessage;
                }
            } else if (error.request) {
                errorMessage = "Pas de réponse du serveur";
            } else {
                errorMessage = error.message || "Erreur de configuration";
            }
            
            onValidateFournisseur(false, errorMessage);
            toast.error(errorMessage);
        }
    };

    return (
        <>
            <Modal isOpen={isOpen} toggle={toggle} size={step === 1 ? "md" : "lg"}>
                <ModalHeader toggle={toggle}>
                    {step === 1 ? "Validation de l'expédition" : "Sélection des fournisseurs"}
                </ModalHeader>
                <ModalBody>
                    {step === 1 ? (
                        <div className="text-center">
                            <Alert color="info" className="mb-3">
                                <i className="ri-information-line me-2"></i>
                                Comment souhaitez-vous valider cette expédition ?
                            </Alert>
                            
                            <div className="d-flex justify-content-center gap-3 mt-4">
                                <Button 
                                    color="warning" 
                                    onClick={() => handleChoice('normal')}
                                    disabled={loading}
                                    size="md"
                                    className="d-flex align-items-center"
                                >
                                    <i className="ri-check-double-line align-bottom me-1"></i>
                                    Valider l'expédition 
                                </Button>
                                <Button 
                                    color="primary" 
                                    onClick={() => handleChoice('fournisseur')}
                                    disabled={loading}
                                    size="md"
                                    className="d-flex align-items-center"
                                >
                                    <i className="ri-user-add-line align-bottom me-1"></i>
                                    Choisir commande fournisseur
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {loadingFournisseurs ? (
                                <div className="text-center py-4">
                                    <Spinner color="primary" />
                                    <div className="mt-2">Chargement des fournisseurs...</div>
                                </div>
                            ) : fournisseursError ? (
                                <Alert color="danger">
                                    <i className="ri-error-warning-line me-2"></i>
                                    {fournisseursError}
                                </Alert>
                            ) : fournisseurs.length === 0 ? (
                                <Alert color="warning">
                                    <i className="ri-alert-line me-2"></i>
                                    Aucun fournisseur disponible
                                </Alert>
                            ) : filteredLines.length === 0 ? (
                                <Alert color="success" className="text-center">
                                    <i className="ri-check-double-line me-2"></i>
                                    Toutes les quantités sont déjà préparées. Aucune ligne à valider.
                                </Alert>
                            ) : (
                                <div className="table-responsive">
                                    <Table bordered hover>
                                        <thead className="table-light">
                                            <tr>
                                                <th>Référence</th>
                                                <th>Produit</th>
                                                <th className="text-center">Qté Restante</th>
                                                <th>Fournisseur</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredLines.map((line: OrderLine) => {
                                                const currentQty = quantities[line.rowid] || 0;
                                                const hasError = validationErrors[line.rowid];
                                                
                                                return (
                                                    <tr key={line.rowid}>
                                                        <td>
                                                            <Badge color="primary">
                                                                {line.fk_product}
                                                            </Badge>
                                                        </td>
                                                        <td style={{ maxWidth: '300px' }}>
                                                            {line.product_label}
                                                        </td>
                                                        <td className="text-center">
                                                            <FormGroup>
                                                                <Input
                                                                    type="number"
                                                                    value={currentQty}
                                                                    onChange={(e) => 
                                                                        handleQteChange(line.rowid, e.target.value)
                                                                    }
                                                                    min={minQte}
                                                                    max={maxQte}
                                                                    step="1"
                                                                    bsSize="sm"
                                                                    invalid={!!hasError}
                                                                    className="text-center"
                                                                />
                                                                {hasError && (
                                                                    <FormFeedback className="d-block text-center">
                                                                        {hasError}
                                                                    </FormFeedback>
                                                                )}
                                                            </FormGroup>
                                                        </td>
                                                        <td>
                                                            <FormGroup>
                                                                <Input
                                                                    type="select"
                                                                    value={selectedFournisseurs[line.rowid] || ""}
                                                                    onChange={(e) =>
                                                                        handleFournisseurChange(
                                                                            line.rowid,
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                    bsSize="sm"
                                                                >
                                                                    {fournisseurs.map((fournisseur: Fournisseur) => (
                                                                        <option
                                                                            key={fournisseur.id}
                                                                            value={fournisseur.id}
                                                                        >
                                                                            {fournisseur.nom} (
                                                                            {fournisseur.code_fournisseur})
                                                                        </option>
                                                                    ))}
                                                                </Input>
                                                            </FormGroup>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                    
                                    <Alert color="info" className="mt-3">
                                        <i className="ri-information-line me-2"></i>
                                        <div>
                                            Une commande fournisseur sera créée pour chaque ligne avec le fournisseur sélectionné.
                                        </div>
                                        <div className="mt-1">
                                            <small>
                                                Quantité acceptée: entre {minQte} et {maxQte} unités par ligne.
                                            </small>
                                        </div>
                                    </Alert>
                                    
                                    {Object.keys(validationErrors).length > 0 && (
                                        <Alert color="danger" className="mt-2">
                                            <i className="ri-error-warning-line me-2"></i>
                                            Veuillez corriger les quantités invalides avant de valider.
                                        </Alert>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    {step === 1 ? (
                        <Button color="secondary" onClick={toggle}>
                            Annuler
                        </Button>
                    ) : (
                        <div className="d-flex justify-content-between w-100">
                            <Button 
                                color="secondary" 
                                onClick={() => setStep(1)}
                                disabled={loading}
                            >
                                <i className="ri-arrow-left-line me-1"></i>
                                Retour
                            </Button>
                            
                            <div className="d-flex gap-2">
                                <Button 
                                    color="secondary" 
                                    onClick={toggle}
                                    disabled={loading}
                                >
                                    Annuler
                                </Button>
                                <Button 
                                    color="primary" 
                                    onClick={handleValidateWithFournisseurs}
                                    disabled={loading || loadingFournisseurs || filteredLines.length === 0 || !isAllQteValid()}
                                >
                                    {loading ? (
                                        <>
                                            <Spinner size="sm" className="me-2" />
                                            Validation...
                                        </>
                                    ) : (
                                        <>
                                            <i className="ri-check-line me-1"></i>
                                            Valider 
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    )}
                </ModalFooter>
            </Modal>


            <Modal isOpen={successModalOpen} toggle={handleCloseSuccessModal} centered>
            <ModalHeader toggle={handleCloseSuccessModal}>
        <i className="ri-checkbox-circle-line text-success me-2"></i>
        Commande(s) fournisseur créée(s) avec succès
    </ModalHeader>
    <ModalBody className="text-center py-4">
        <div className="mb-3">
            <i className="ri-check-double-line display-1 text-success"></i>
        </div>
        <h4>Votre commande a été enregistrée</h4>
        
        {/* Résumé du traitement */}
        <div className="bg-light p-3 rounded mt-3 mb-3">
            <div className="row">
                <div className="col-6 text-center">
                    <div className="text-success fw-bold fs-4">
                        {successModalData.successLinesCount}
                    </div>
                    <div className="text-muted small">Ligne(s) traitée(s)</div>
                </div>
                <div className="col-6 text-center">
                    <div className="text-primary fw-bold fs-4">
                        {successModalData.supplierOrdersCount}
                    </div>
                    <div className="text-muted small">Commande(s) créée(s)</div>
                </div>
            </div>
            
            {successModalData.errorLinesCount > 0 && (
                <div className="mt-3 text-center">
                    <div className="text-warning fw-bold fs-4">
                        {successModalData.errorLinesCount}
                    </div>
                    <div className="text-muted small">Erreur(s)</div>
                </div>
            )}
        </div>
        
        {/* Références des commandes - SUR LA MÊME LIGNE */}
        {successModalData.orderRefs.length > 0 && (
            <>
                <p className="text-muted mt-3 mb-2">
                    Référence{successModalData.orderRefs.length > 1 ? 's' : ''} de{successModalData.orderRefs.length > 1 ? 's' : ''} commande{successModalData.orderRefs.length > 1 ? 's' : ''} :
                </p>
                <div className="bg-light p-3 rounded">
                    <strong className="fs-16 text-primary">
                        {successModalData.orderRefs.join(', ')}
                    </strong>
                </div>
            </>
        )}
    </ModalBody>
    <ModalFooter className="justify-content-center">
        <Button color="primary" onClick={handleCloseSuccessModal}>
            Fermer
        </Button>
    </ModalFooter>
</Modal>
        </>
    );
};

export default ValidationChoiceModal;